<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
    :max-width="dialogMaxWidth"
    content-class="solvit-dialog-form-shell altitude-estimate-dialog"
    transition="dialog-transition"
    @input="$emit('input', $event)"
  >
    <v-card
      class="d-flex flex-column altitude-estimate-modal"
      :style="{ maxHeight: dialogMaxHeight, height: dialogMaxHeight }"
      rounded="lg"
      elevation="8"
    >
      <v-card-title class="py-3 px-4 d-flex align-center flex-grow-0">
        <span class="text-h6">{{ $t('altitude.estimateModalTitle') }}</span>
        <v-spacer />
        <v-chip small class="mr-2" data-testid="altitude-estimate-sample-count">
          {{
            $t('altitude.estimateSampleCount', {
              n: includedSampleCount,
              min: minSamples
            })
          }}
        </v-chip>
        <span v-if="reportId" class="caption text--secondary mr-3">
          {{ $t('altitude.estimateReportLabel', { id: reportId }) }}
        </span>
        <v-btn icon :aria-label="$t('common.close')" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="flex-grow-1 d-flex flex-column pa-4 overflow-y-auto">
        <v-alert type="info" dense text class="mb-3 flex-grow-0">
          {{ $t('altitude.estimateScaleOnlyNote') }}
        </v-alert>
        <v-alert type="info" dense outlined class="mb-3 flex-grow-0">
          {{ $t('altitude.estimateSyncHint') }}
        </v-alert>
        <v-alert
          v-if="addSampleError"
          type="warning"
          dense
          dismissible
          class="mb-3 flex-grow-0"
          @input="addSampleError = null"
        >
          {{ addSampleError }}
        </v-alert>

        <v-row class="flex-grow-1" dense style="min-height: 300px">
          <v-col cols="12" md="6" class="d-flex">
            <v-card outlined class="flex-grow-1 d-flex flex-column" rounded>
              <v-card-title class="subtitle-2 py-2">
                {{ $t('altitude.estimateOrthoPanel') }}
              </v-card-title>
              <v-divider />
              <altitude-ortho-box-map
                v-if="value"
                ref="orthoMap"
                :thermal-images="thermalImages"
                :site="site"
                :focus-image="focusImage"
                @box-change="onOrthoBoxChange"
              />
            </v-card>
          </v-col>
          <v-col cols="12" md="6" class="d-flex">
            <v-card outlined class="flex-grow-1 d-flex flex-column" rounded>
              <v-card-title class="subtitle-2 py-2">
                {{ $t('altitude.estimateIrPanel') }}
              </v-card-title>
              <v-divider />
              <altitude-ir-box-panel
                v-if="value"
                ref="irPanel"
                :thermal-images="thermalImages"
                @box-change="onIrBoxChange"
                @image-change="onIrImageChange"
              />
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="mt-3 flex-grow-0" rounded>
          <v-card-title class="subtitle-2 py-2">
            {{ $t('altitude.estimateSamplesTitle') }}
          </v-card-title>
          <v-divider />
          <v-simple-table dense data-testid="altitude-estimate-sample-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t('altitude.estimateColFile') }}</th>
                <th>{{ $t('altitude.estimateColLMap') }}</th>
                <th>{{ $t('altitude.estimateColNPx') }}</th>
                <th>{{ $t('altitude.estimateColHStar') }}</th>
                <th>{{ $t('altitude.estimateColDeltaH') }}</th>
                <th>{{ $t('altitude.estimateColUsed') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="samples.length === 0">
                <td colspan="7" class="text-center text--secondary py-4">
                  {{ $t('altitude.estimateSamplesEmpty') }}
                </td>
              </tr>
              <tr v-for="(row, index) in samples" :key="row.id">
                <td>{{ index + 1 }}</td>
                <td>{{ row.imgName || row.thermalImageId }}</td>
                <td>{{ formatNum(row.L_map_m) }}</td>
                <td>{{ formatNum(row.N_px, 0) }}</td>
                <td>{{ formatNum(row.hStar_m) }}</td>
                <td>{{ formatNum(row.deltaH_m) }}</td>
                <td>
                  <v-checkbox
                    :input-value="row.included !== false"
                    hide-details
                    dense
                    class="ma-0"
                    @change="toggleIncluded(row.id, $event)"
                  />
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3 flex-wrap flex-grow-0">
        <v-btn
          outlined
          color="primary"
          :disabled="!canAddSample"
          data-testid="altitude-estimate-add-sample"
          @click="addSample"
        >
          {{ $t('altitude.estimateAddSample') }}
        </v-btn>
        <v-spacer />
        <v-btn text @click="close">{{ $t('common.close') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!canApply"
          data-testid="altitude-estimate-apply"
          @click="emitApply"
        >
          {{ $t('altitude.estimateApplyToAi') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import {
    aggregateDeltaH,
    buildAltitudeEstimateResult,
    computeSampleAltitude,
    flagOutlierIds,
    footprintWidthFromImgPosCoords,
    isValidSampleInput
  } from '@/features/altitude-estimate/estimateIrAltitudeFromBoxes';
  import { MODAL_LAYOUT_SPECIAL } from '@/constants/modalLayout';
  import AltitudeOrthoBoxMap from '@/features/altitude-estimate/components/AltitudeOrthoBoxMap.vue';
  import AltitudeIrBoxPanel from '@/features/altitude-estimate/components/AltitudeIrBoxPanel.vue';

  const MIN_SAMPLES = 3;

  export default {
    name: 'AltitudeEstimateModal',
    components: {
      AltitudeOrthoBoxMap,
      AltitudeIrBoxPanel
    },
    props: {
      value: { type: Boolean, default: false },
      reportId: { type: [Number, String], default: null }
    },
    data() {
      return {
        minSamples: MIN_SAMPLES,
        samples: [],
        sampleSeq: 0,
        orthoBox: null,
        irPayload: null,
        focusImage: null,
        addSampleError: null,
        dialogMaxWidth: MODAL_LAYOUT_SPECIAL.AI_THERMAL_COMPARE_MAX,
        dialogMaxHeight: 'min(900px, calc(100vh - 48px))'
      };
    },
    computed: {
      ...mapGetters('dashboard', {
        thermalImages: 'getThermalImages'
      }),
      ...mapGetters('explorer', {
        site: 'getSite'
      }),
      includedSampleCount() {
        return this.samples.filter(
          s => s.included !== false && Number.isFinite(Number(s.deltaH_m))
        ).length;
      },
      aggregate() {
        return aggregateDeltaH(this.samples);
      },
      canApply() {
        return this.aggregate != null;
      },
      canAddSample() {
        const L = this.orthoBox?.L_map_m;
        const N = this.irPayload?.irBox?.N_px;
        return isValidSampleInput({ L_map_m: L, N_px: N }) && !!this.irPayload?.image;
      }
    },
    watch: {
      value(open) {
        if (open) {
          this.samples = [];
          this.sampleSeq = 0;
          this.orthoBox = null;
          this.irPayload = null;
          this.focusImage = null;
          this.addSampleError = null;
          this.ensureThermalImages();
        }
      }
    },
    methods: {
      ...mapActions('dashboard', ['fetchThermalImages']),

      async ensureThermalImages() {
        if ((this.thermalImages || []).length > 0) {
          return;
        }
        if (this.reportId == null) {
          return;
        }
        try {
          await this.fetchThermalImages({ reportId: this.reportId });
        } catch (e) {
          console.warn('[AltitudeEstimateModal] fetchThermalImages', e);
        }
      },

      close() {
        this.$emit('input', false);
      },

      onOrthoBoxChange(box) {
        this.orthoBox = box;
        this.addSampleError = null;
      },

      onIrBoxChange(payload) {
        this.irPayload = payload;
        this.addSampleError = null;
        if (payload?.image) {
          this.focusImage = payload.image;
        }
      },

      onIrImageChange(image) {
        this.focusImage = image || null;
        this.irPayload = null;
        this.addSampleError = null;
      },

      toggleIncluded(id, included) {
        const row = this.samples.find(s => s.id === id);
        if (!row) {
          return;
        }
        this.$set(row, 'included', !!included);
      },

      applyOutlierDefaults(nextSamples) {
        const outlierIds = new Set(flagOutlierIds(nextSamples));
        return nextSamples.map(s => ({
          ...s,
          included: s.included === false ? false : !outlierIds.has(String(s.id))
        }));
      },

      addSample() {
        this.addSampleError = null;
        if (!this.canAddSample) {
          return;
        }
        const image = this.irPayload.image;
        const L_map_m = Number(this.orthoBox.L_map_m);
        const N_px = Number(this.irPayload.irBox.N_px);
        const imgWidth = Number(image.imgWidth) || Number(this.irPayload.irBox.imgWidth) || 640;
        const footprintWidth_m = footprintWidthFromImgPosCoords(image.imgPosCoords);
        const h0_m =
          image.relativeAltitude != null && image.relativeAltitude !== ''
            ? Number(image.relativeAltitude)
            : null;

        if (h0_m == null || !Number.isFinite(h0_m)) {
          this.addSampleError = this.$t('altitude.estimateAddSampleNoH0');
          return;
        }
        if (footprintWidth_m == null || !Number.isFinite(footprintWidth_m)) {
          this.addSampleError = this.$t('altitude.estimateAddSampleNoFootprint');
          return;
        }

        const calc = computeSampleAltitude({
          L_map_m,
          N_px,
          imgWidth,
          footprintWidth_m,
          h0_m
        });
        if (!calc) {
          this.addSampleError = this.$t('altitude.estimateAddSampleInvalid');
          return;
        }
        if (calc.deltaH_m == null) {
          this.addSampleError = this.$t('altitude.estimateAddSampleNoDelta');
          return;
        }

        this.sampleSeq += 1;
        const row = {
          id: `s${this.sampleSeq}`,
          thermalImageId: image.id,
          imgName: image.imgName || String(image.id),
          L_map_m,
          N_px,
          gsd_m: calc.gsd_m,
          h0_m,
          hStar_m: calc.hStar_m,
          deltaH_m: calc.deltaH_m,
          included: true,
          orthoBox: this.orthoBox,
          irBox: this.irPayload.irBox
        };
        this.samples = this.applyOutlierDefaults([...this.samples, row]);
        if (this.$refs.orthoMap?.reset) {
          this.$refs.orthoMap.reset();
        }
        if (this.$refs.irPanel?.reset) {
          this.$refs.irPanel.reset();
        }
        this.orthoBox = null;
        this.irPayload = null;
      },

      emitApply() {
        if (!this.canApply) {
          return;
        }
        const result = buildAltitudeEstimateResult({
          reportId: this.reportId,
          samples: this.samples
        });
        if (!result) {
          return;
        }
        this.$emit('apply', result);
      },

      formatNum(value, digits = 2) {
        const n = Number(value);
        if (!Number.isFinite(n)) {
          return '—';
        }
        return digits === 0 ? String(Math.round(n)) : n.toFixed(digits);
      }
    }
  };
</script>
